import React from "react";
import { useSurveyStatus } from "../../hooks/useSurveyStatus";
import { useNavigate } from "react-router-dom";
import { ProgressBar, Button, Typography } from "../../components/common";
import { Clock, CheckCircle, BookOpen } from "lucide-react";
import { MasterCourseWithProgress } from "../../store/api/masterCourseApi";
import SuggestionBoardImage from "../../images/SuggestionBoardImage";
import { PedagogueMark } from "@pedagogue/ui-core";
import { normalizePublicImageUrl } from "../../utils/imageUrl";
import { splitCourseTitle } from "../../utils/courseTitle";
import { formatRemainingMinutesLeft, formatTimeLeft } from "../../utils/progressUtils";

interface MasterCourseCardProps {
  masterCourse: MasterCourseWithProgress;
  showCertifiedBadge?: boolean;
  organisationLogoUrl?: string | null;
}

export const MasterCourseCard: React.FC<MasterCourseCardProps> = ({
  masterCourse,
  showCertifiedBadge = true,
  organisationLogoUrl,
}) => {
  const navigate = useNavigate();
  const { status: surveyStatus } = useSurveyStatus();
  const isCompleted = masterCourse.isCompleted;
  const { primaryTitle, attribution } = splitCourseTitle(masterCourse.title);

  const handleClick = () => {
    const returnTo = `/master-courses/${masterCourse.id}`;
    if (surveyStatus?.baselineRequired) {
      navigate("/survey/start", { state: { returnTo } });
    } else {
      navigate(returnTo);
    }
  };

  // Calculate remaining time from incomplete levels only (same logic as detail page)
  const remainingMinutes = masterCourse.levels.reduce((sum, level) => {
    if (level.status === "locked_by_plan" || level.isCompleted) return sum;
    const levelMinutes = level.estimatedDurationMinutes || 0;
    const levelRemaining = Math.round(levelMinutes * (1 - level.progressPercentage / 100));
    return sum + levelRemaining;
  }, 0);

  // Check if we have valid duration data
  const hasDurationData = masterCourse.levels.some(
    level => level.status !== "locked_by_plan" && level.estimatedDurationMinutes > 0
  );

  const timeDisplay = hasDurationData && !isCompleted
    ? formatRemainingMinutesLeft(remainingMinutes)
    : formatTimeLeft(masterCourse.progressPercentage, 0, isCompleted);
  // The organisation's branding takes precedence; the course icon is the fallback.
  const iconUrl = organisationLogoUrl ? organisationLogoUrl : normalizePublicImageUrl(masterCourse.iconUri);
  const moduleTitles = [...masterCourse.levels].sort(
    (a, b) => a.levelNumber - b.levelNumber,
  );

  return (
    <div
      className={`relative bg-white rounded-2xl shadow-sm flex flex-col w-full h-full min-h-[320px] p-6 overflow-hidden border cursor-pointer transition-all hover:shadow-md ${
        isCompleted ? "border-green-200" : "border-slate-300"
      }`}
      onClick={handleClick}
    >
      {/* Completed badge */}
      {isCompleted && (
        <div className="absolute top-6 right-6 flex items-center gap-1 text-green-600 bg-green-100 px-3 py-1 rounded-full">
          <CheckCircle size={16} />
          <span className="text-sm font-medium">Complete</span>
        </div>
      )}

      {/* Modules count badge */}
      {!isCompleted && (
        <div className="absolute top-6 right-6 flex items-center gap-1 text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
          <BookOpen size={16} />
          <span className="text-sm font-medium">
            {masterCourse.completedLevels}/{masterCourse.totalLevels} Modules
          </span>
        </div>
      )}

      {/* Pedagogue and subscribing-school marks */}
      <div className="w-full h-[160px] md:h-[180px] flex items-center gap-4 bg-transparent">
        <div className="w-[72px] shrink-0 flex items-center justify-center">
          <PedagogueMark size={56} />
        </div>
        <div aria-hidden="true" className="h-[96px] border-l border-slate-200" />
        <div className="min-w-0 flex-1 h-[144px] flex items-center justify-center overflow-hidden">
          {iconUrl ? (
            <img
              src={iconUrl}
              alt="Your school logo"
              className="max-h-[96px] max-w-[120px] object-contain rounded-lg"
            />
          ) : (
            <SuggestionBoardImage />
          )}
        </div>
      </div>

      {/* Course info */}
      <div className="flex flex-col flex-1 w-full gap-2">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <Typography
              variant="h4"
              weight="bold"
              className="text-gray-900 line-clamp-2"
            >
              {primaryTitle}
            </Typography>
            {attribution && (
              <Typography variant="small" className="mt-1 text-gray-500 line-clamp-2">
                {attribution}
              </Typography>
            )}
          </div>
          {showCertifiedBadge && masterCourse.isPedagogueCertified && (
            <span className="shrink-0 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
              Pedagogue Certified
            </span>
          )}
        </div>

        {moduleTitles.length > 0 && (
          <ol
            data-testid="course-card-module-list"
            className="my-2 space-y-1 border-y border-slate-100 py-3"
          >
            {moduleTitles.map((module) => (
              <li
                key={module.id}
                className="flex gap-3 text-sm leading-snug text-slate-700"
              >
                <span className="shrink-0 font-medium text-slate-400">
                  {String(module.levelNumber).padStart(2, "0")}
                </span>
                <span>{module.title}</span>
              </li>
            ))}
          </ol>
        )}

        {/* Overall progress bar */}
        <ProgressBar progress={masterCourse.progressPercentage} />

        {/* Separator */}
        <div className="border-t border-gray-200 my-2"></div>

        {/* Actions and Info */}
        <div className="flex items-center justify-start gap-4">
          <Button
            buttonType="button"
            label={surveyStatus?.baselineRequired ? "Take the baseline survey" : isCompleted ? "Review" : masterCourse.progressPercentage > 0 ? "Continue" : "Start"}
            variant="filled"
            className="w-[160px] lg:w-[180px] font-semibold px-6 py-2 bg-primary text-white hover:bg-primary/90 transition"
            onClick={handleClick}
          />
          <div className="flex items-center gap-2 text-gray-500 text-base font-medium">
            <Clock size={20} strokeWidth={1} />
            {timeDisplay}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterCourseCard;
